import type { SVGProps } from "react";

import { cn } from "../cn";

/** Social platforms with a mark of their own. */
export type SocialMark = "youtube" | "facebook" | "instagram" | "tiktok" | "linkedin" | "google";

/** Each platform's brand colour, for the mark's tile. */
export const SOCIAL_COLOURS: Record<SocialMark, string> = {
  youtube: "#ff0033",
  facebook: "#1877f2",
  instagram: "#d62976",
  tiktok: "#111111",
  linkedin: "#0a66c2",
  google: "#4285f4",
};

/**
 * Simple marks drawn for BrandEx (lucide no longer ships brand icons). They
 * only identify the platform; labels always say its name too.
 */
function Mark({ platform }: { platform: SocialMark }) {
  switch (platform) {
    case "youtube":
      return (
        <>
          <rect x="2" y="5" width="20" height="14" rx="4.5" fill="currentColor" />
          <path d="M10 9.2v5.6l5-2.8z" fill="#fff" />
        </>
      );
    case "facebook":
      return (
        <>
          <circle cx="12" cy="12" r="10" fill="currentColor" />
          <path
            d="M13.2 21.9v-7.2h2.4l.4-2.9h-2.8V10c0-.8.3-1.4 1.4-1.4H16V6.1c-.3 0-1.1-.1-2.1-.1-2.1 0-3.6 1.3-3.6 3.7v2.1H8v2.9h2.4v7.2"
            fill="#fff"
          />
        </>
      );
    case "instagram":
      return (
        <>
          <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="17.4" cy="6.6" r="1.25" fill="currentColor" />
        </>
      );
    case "linkedin":
      return (
        <>
          <rect x="2" y="2" width="20" height="20" rx="3.5" fill="currentColor" />
          <path d="M6.4 9.6h2.5v8H6.4zM7.65 5.6a1.45 1.45 0 1 1 0 2.9 1.45 1.45 0 0 1 0-2.9zM10.5 9.6h2.4v1.1c.4-.7 1.3-1.3 2.6-1.3 2.6 0 3.1 1.7 3.1 3.9v4.3h-2.5v-3.8c0-.9 0-2.1-1.3-2.1s-1.5 1-1.5 2v3.9h-2.8z" fill="#fff" />
        </>
      );
    case "google":
      return (
        <>
          <path d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z" fill="#4285f4" />
          <path d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z" fill="#34a853" />
          <path d="M6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3.1a10 10 0 0 0 0 9.2z" fill="#fbbc05" />
          <path d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.4L6.4 10c.8-2.3 3-4.1 5.6-4.1z" fill="#ea4335" />
        </>
      );
    case "tiktok":
      return (
        <path
          d="M14.6 2h3a4.6 4.6 0 0 0 3.9 4v3.1a7.6 7.6 0 0 1-3.9-1.2v6.6a6.5 6.5 0 1 1-6.5-6.5c.3 0 .6 0 .9.1v3.2a3.4 3.4 0 1 0 2.6 3.2z"
          fill="currentColor"
        />
      );
  }
}

/** A platform's mark in its brand colour (or `currentColor` when `plain`). */
export function PlatformIcon({
  platform,
  plain = false,
  className,
  ...props
}: { platform: SocialMark; plain?: boolean } & Omit<SVGProps<SVGSVGElement>, "children">) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      className={cn("h-5 w-5 shrink-0", className)}
      style={plain ? undefined : { color: SOCIAL_COLOURS[platform] }}
      {...props}
    >
      <Mark platform={platform} />
    </svg>
  );
}
